// geocode.ts
// Geocodes an Australian property address to lat/lng using Nominatim (OpenStreetMap).
// Free — no API key required. Rate limit: 1 request/second per Nominatim policy.

interface GeoResult {
  latitude: number;
  longitude: number;
}

export async function geocodeAddress(
  address: string,
  suburb: string,
  state: string,
  postcode: string,
): Promise<GeoResult | null> {
  const parts = [address, suburb, state, postcode, 'Australia'].filter(Boolean);
  if (parts.length < 3) return null;

  const params = new URLSearchParams({
    q: parts.join(', '),
    format: 'json',
    limit: '1',
    countrycodes: 'au',
  });

  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?${params.toString()}`,
      { headers: { 'User-Agent': 'AusProp/1.0 (propertyoshq@gmail.com)' } },
    );
    if (!res.ok) return null;
    const results = (await res.json()) as Array<{ lat: string; lon: string }>;
    if (!results.length) return null;
    const lat = parseFloat(results[0].lat);
    const lon = parseFloat(results[0].lon);
    if (isNaN(lat) || isNaN(lon)) return null;
    return { latitude: lat, longitude: lon };
  } catch {
    return null;
  }
}

// Backfills lat/lng for a list of properties that are missing coordinates.
// Processes sequentially with 1.1s delay to respect Nominatim rate limit.
// Calls onGeocoded(propertyId, lat, lng) for each successful result.
export async function backfillMissingCoordinates(
  properties: Array<{
    id: string;
    address?: string;
    suburb?: string;
    state?: string;
    postcode?: string;
    latitude?: number | null;
    longitude?: number | null;
  }>,
  onGeocoded: (id: string, latitude: number, longitude: number) => void,
): Promise<void> {
  const needsGeocode = properties.filter(
    p => !p.latitude && !p.longitude && p.address && p.suburb && p.state,
  );
  if (needsGeocode.length === 0) return;

  for (const prop of needsGeocode) {
    const result = await geocodeAddress(
      prop.address ?? '',
      prop.suburb ?? '',
      prop.state ?? '',
      prop.postcode ?? '',
    );
    if (result) {
      onGeocoded(prop.id, result.latitude, result.longitude);
    }
    // Respect Nominatim's 1 req/sec policy
    await new Promise<void>(resolve => setTimeout(resolve, 1100));
  }
}
